/**
 * MealPlanPricingTable Component
 * 
 * Displays pricing grouped by meal plan for Custom/Manual/Single-DMC packages.
 * Supports both 'person_wise' and 'total' formats (matches canonical
 * `quote_line_items.quotation_format` vocabulary).
 * 
 * Format: total + showBreakup=true
 *   | Category | Hotel Cost | Transfers | Total Package | Discount | GST (X%) | Grand Total |
 * 
 * Format: total + showBreakup=false
 *   | Category | Total Package | Discount | GST (X%) | Grand Total |
 * 
 * Format: person_wise
 *   | Category | Adult (xN) | Extra Bed (xN) | Child Bed (xN) | Child No Bed (xN) | Infant (xN) | Total | Discount | GST (X%) | Grand Total |
 * 
 * Features:
 * - Meal plan header ("Breakfast Only", "Breakfast + Dinner")
 * - "Transfers" = Vehicle Transfers + P2P Transfers (combined)
 * - Dynamic pax/discount column hiding
 * - GST column hidden when gst_included = true
 * 
 * Usage:
 *   <MealPlanPricingTable
 *     mealPlan="CP"
 *     mealPlanLabel="Breakfast Only"
 *     categories={[{ name: 'Deluxe', hotelCost, transferCost, ... }]}
 *     format="total"
 *     showBreakup={true}
 *     paxConfig={{ adults: 2, extraBed: 0, childWithBed: 1, childNoBed: 0, infants: 0 }}
 *     gstConfig={{ percentage: 5, included: false, applyOn: 'total' }}
 *     variant="detail"
 *   />
 */

import React from 'react';
import { COMPONENT_VARIANTS, formatCurrency } from './displayConfig';
import PricingMobileCards from './PricingMobileCards';

const MealPlanPricingTable = ({
  mealPlan = '',
  mealPlanLabel = '',
  categories = [],
  format = 'total',
  showBreakup = true,
  paxConfig = {},
  gstConfig = {},
  discountConfig = {},
  variant = 'detail',
  className = ''
}) => {
  if (!categories || categories.length === 0) {
    return null;
  }

  const styles = COMPONENT_VARIANTS[variant] || COMPONENT_VARIANTS.detail;
  // Accept both canonical 'per_person' and legacy 'person_wise' spellings.
  const isPersonWise = format === 'person_wise' || format === 'per_person';
  
  // Pax configuration
  const { 
    adults = 0, 
    extraBed = 0, 
    childWithBed = 0, 
    childNoBed = 0, 
    infants = 0 
  } = paxConfig;
  
  // GST configuration
  const { 
    percentage: gstPct = 5, 
    included: gstIncluded = false,
    applyOn = 'total'
  } = gstConfig;

  // Discount configuration — Bug 2 (2026-02): gate the Discount column on
  // `show_discount_separately`. When OFF (default), the prices customer sees
  // are already net-of-discount and the column is hidden entirely.
  const { showSeparately: showDiscountSeparately = false } = discountConfig;
  
  // Determine which pax columns to show (for person_wise format)
  const hasExtraBed = extraBed > 0;
  const hasChildWithBed = childWithBed > 0;
  const hasChildNoBed = childNoBed > 0;
  const hasInfants = infants > 0;
  
  // Check if any category has discount AND operator elected to display it separately
  const hasDiscount = showDiscountSeparately && categories.some(cat => (cat.discount || 0) > 0);

  // Render total format table
  const renderTotalAmountTable = () => (
    <table className={`w-full ${styles.fontSize}`}>
      <thead>
        <tr className="bg-gray-100">
          <th className={`border-b ${styles.padding} text-left font-semibold`}>Category</th>
          {showBreakup && (
            <>
              <th className={`border-b ${styles.padding} text-right font-semibold`}>Hotel Cost</th>
              <th className={`border-b ${styles.padding} text-right font-semibold`}>Transfers</th>
            </>
          )}
          {/* Week-4 (2026-06-28) — When GST is included, "Total Package" is
              identical to Grand Total (pre-GST base + 0 add-on GST = post-GST
              total, because GST is already embedded). Hide the duplicate
              column and let the Grand Total column carry the canonical
              "Package Price (GST @ N% included)" label. */}
          {!gstIncluded && (
            <th className={`border-b ${styles.padding} text-right font-semibold`}>Total Package</th>
          )}
          {hasDiscount && (
            <th className={`border-b ${styles.padding} text-right font-semibold text-[var(--navy)]`}>Discount</th>
          )}
          {!gstIncluded && gstPct > 0 && (
            <th className={`border-b ${styles.padding} text-right font-semibold text-gray-600`}>
              GST ({gstPct}%)
            </th>
          )}
          <th className={`border-b ${styles.padding} text-right font-semibold bg-[color-mix(in_srgb,var(--navy)_15%,white)] text-[var(--navy)]`}>
            {gstIncluded && gstPct > 0
              ? <>Package Price<br/><span className="font-normal text-xs">(GST @ {gstPct}% included)</span></>
              : 'Grand Total'}
          </th>
        </tr>
      </thead>
      <tbody>
        {categories.map((cat, idx) => {
          // Bug D1 fix (2026-02): cat.net_quoted_price is ALREADY post-discount.
          // The Save-view was previously subtracting `discount` again and then
          // computing GST on the doubly-reduced amount → Grand Total ₹1,710
          // lower than Preview + PDF for a ₹1,629 discount. Correct math:
          //   net           = post-discount (comes from backend)
          //   displayedTotal= net + discount when showing discount separately
          //                   (so the "− Discount" column reconciles visually);
          //                   otherwise `net` (customer-facing net price).
          //   gstBase       = net (post-discount) — matches Preview + PDF.
          //   grandTotal    = net + gst (or net when gst_included).
          const net = cat.totalPackage || cat.net_quoted_price || cat.total_package_price || 0;
          const discount = cat.discount || 0;
          const displayedTotal = showDiscountSeparately ? (net + discount) : net;

          // Calculate GST on the true post-discount base
          const gstBase = applyOn === 'markup' ? (cat.markup_amount || 0) : net;
          const gstAmount = gstPct > 0 ? gstBase * (gstPct / 100) : 0;

          // Grand total always includes GST when not embedded
          // Ticket #285275 (Option A): included + stored GST > 0 means GST was
          // added to the price (legacy included quotes store 0).
          const storedGst = Number(cat.gst_amount || cat.gstAmount || 0);
          const gstInTotal = gstIncluded && storedGst > 0;
          const grandTotal = (gstIncluded && !gstInTotal) ? net : net + (gstInTotal ? storedGst : gstAmount);
          
          return (
            <tr key={cat.name || idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
              <td className={`border-b ${styles.padding} font-medium`}>
                {(cat.name || cat.category || '').replace(/_/g, ' ')}
              </td>
              {showBreakup && (
                <>
                  <td className={`border-b ${styles.padding} text-right`}>
                    {cat.hotelCost > 0 ? formatCurrency(cat.hotelCost) : 
                      <span className="text-gray-500 italic text-xs">Included</span>}
                  </td>
                  <td className={`border-b ${styles.padding} text-right`}>
                    {cat.transfers > 0 ? formatCurrency(cat.transfers) : 
                      <span className="text-gray-500 italic text-xs">Included</span>}
                  </td>
                </>
              )}
              {!gstIncluded && (
                <td className={`border-b ${styles.padding} text-right font-bold text-[var(--navy)]`}>
                  {formatCurrency(displayedTotal)}
                </td>
              )}
              {hasDiscount && (
                <td className={`border-b ${styles.padding} text-right font-semibold text-[var(--navy)]`}>
                  {discount > 0 ? `- ${formatCurrency(discount)}` : '-'}
                </td>
              )}
              {!gstIncluded && gstPct > 0 && (
                <td className={`border-b ${styles.padding} text-right text-gray-600`}>
                  {formatCurrency(gstAmount)}
                </td>
              )}
              <td className={`border-b ${styles.padding} text-right font-bold text-[var(--navy)] bg-[color-mix(in_srgb,var(--navy)_10%,white)]`}>
                {formatCurrency(grandTotal)}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );

  // Render person_wise format table
  const renderPersonWiseTable = () => (
    <table className={`w-full ${styles.fontSize}`}>
      <thead>
        <tr className="bg-gray-100">
          <th className={`border-b ${styles.padding} text-left font-semibold`}>Category</th>
          <th className={`border-b ${styles.padding} text-right bg-[color-mix(in_srgb,var(--navy)_10%,white)] text-[var(--navy)]`}>
            Adult<br/><span className="font-normal text-xs">x{adults}</span>
          </th>
          {hasExtraBed && (
            <th className={`border-b ${styles.padding} text-right bg-[color-mix(in_srgb,var(--navy)_10%,white)] text-[var(--navy)]`}>
              Extra Bed<br/><span className="font-normal text-xs">x{extraBed}</span>
            </th>
          )}
          {hasChildWithBed && (
            <th className={`border-b ${styles.padding} text-right bg-[color-mix(in_srgb,var(--cyan)_10%,white)] text-[var(--navy)]`}>
              Child (Bed)<br/><span className="font-normal text-xs">x{childWithBed}</span>
            </th>
          )}
          {hasChildNoBed && (
            <th className={`border-b ${styles.padding} text-right bg-[color-mix(in_srgb,var(--cyan)_10%,white)] text-[var(--navy)]`}>
              Child (No Bed)<br/><span className="font-normal text-xs">x{childNoBed}</span>
            </th>
          )}
          {hasInfants && (
            <th className={`border-b ${styles.padding} text-right bg-[color-mix(in_srgb,var(--navy)_10%,white)] text-[var(--navy)]`}>
              Infant<br/><span className="font-normal text-xs">x{infants}</span>
            </th>
          )}
          <th className={`border-b ${styles.padding} text-right font-semibold`}>Total</th>
          {hasDiscount && (
            <th className={`border-b ${styles.padding} text-right font-semibold text-[var(--navy)]`}>Discount</th>
          )}
          {!gstIncluded && gstPct > 0 && (
            <th className={`border-b ${styles.padding} text-right text-gray-600`}>
              GST<br/><span className="font-normal text-xs">{gstPct}%</span>
            </th>
          )}
          <th className={`border-b ${styles.padding} text-right bg-[color-mix(in_srgb,var(--navy)_15%,white)] text-[var(--navy)]`}>
            Grand Total
            {gstIncluded && gstPct > 0 && (
              <><br/><span className="font-normal text-xs">incl. GST</span></>
            )}
          </th>
        </tr>
      </thead>
      <tbody>
        {categories.map((cat, idx) => {
          const adultPrice = cat.adultPrice || cat.adult_price || cat.per_person_price || 0;
          const extraBedPrice = cat.extraBedPrice || cat.adult_extra_bed_price || 0;
          const childBedPrice = cat.childBedPrice || cat.child_with_bed_price || 0;
          const childNoBedPrice = cat.childNoBedPrice || cat.child_no_bed_price || 0;
          const infantPrice = cat.infantPrice || cat.infant_price || 0;
          
          // Calculate total from per-person prices
          const calculatedTotal = (adultPrice * adults) +
                       (extraBedPrice * extraBed) +
                       (childBedPrice * childWithBed) +
                       (childNoBedPrice * childNoBed) +
                       (infantPrice * infants);
          
          // Use stored net_quoted_price if available (includes P2P costs), otherwise use calculated
          // This ensures consistency with PDF which uses net_quoted_price
          const total = cat.net_quoted_price || cat.totalPackage || calculatedTotal;
          
          const discount = cat.discount || 0;
          const afterDiscount = total - discount;
          
          // Calculate GST - use stored gst_amount if available
          const storedGstAmount = cat.gst_amount || cat.gstAmount || 0;
          const gstBase = applyOn === 'markup' ? (cat.markup_amount || 0) : afterDiscount;
          const gstAmount = storedGstAmount > 0 ? storedGstAmount : (gstPct > 0 ? gstBase * (gstPct / 100) : 0);
          
          // Grand total = Total + GST (when GST is not included)
          // Don't use stored total_with_gst as it may be calculated from different base
          // Ticket #285275 (Option A): stored GST > 0 with "included" = GST added.
          const grandTotal = (gstIncluded && !(storedGstAmount > 0)) ? total : total + gstAmount;
          
          return (
            <tr key={cat.name || idx}>
              <td className={`border-b ${styles.padding} font-medium`}>
                {(cat.name || cat.category || '').replace(/_/g, ' ')}
              </td>
              <td className={`border-b ${styles.padding} text-right font-semibold text-[var(--navy)]`}>
                {formatCurrency(adultPrice)}
              </td>
              {hasExtraBed && (
                <td className={`border-b ${styles.padding} text-right font-semibold text-[var(--navy)]`}>
                  {extraBedPrice > 0 ? formatCurrency(extraBedPrice) : '-'}
                </td>
              )}
              {hasChildWithBed && (
                <td className={`border-b ${styles.padding} text-right font-semibold text-[var(--navy)]`}>
                  {childBedPrice > 0 ? formatCurrency(childBedPrice) : '-'}
                </td>
              )}
              {hasChildNoBed && (
                <td className={`border-b ${styles.padding} text-right font-semibold text-[var(--navy)]`}>
                  {childNoBedPrice > 0 ? formatCurrency(childNoBedPrice) : '-'}
                </td>
              )}
              {hasInfants && (
                <td className={`border-b ${styles.padding} text-right font-semibold text-[var(--navy)]`}>
                  {infantPrice > 0 ? formatCurrency(infantPrice) : '-'}
                </td>
              )}
              <td className={`border-b ${styles.padding} text-right font-bold text-[var(--navy)]`}>
                {formatCurrency(total)}
              </td>
              {hasDiscount && (
                <td className={`border-b ${styles.padding} text-right font-semibold text-[var(--navy)]`}>
                  {discount > 0 ? `- ${formatCurrency(discount)}` : '-'}
                </td>
              )}
              {!gstIncluded && gstPct > 0 && (
                <td className={`border-b ${styles.padding} text-right text-gray-600`}>
                  {formatCurrency(gstAmount)}
                </td>
              )}
              <td className={`border-b ${styles.padding} text-right font-bold text-[var(--navy)] bg-[color-mix(in_srgb,var(--navy)_10%,white)]`}>
                {formatCurrency(grandTotal)}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );

  return (
    <div 
      className={`border border-gray-200 rounded-lg overflow-hidden ${className}`}
      data-component="meal-plan-pricing-table"
      data-meal-plan={mealPlan}
      data-format={format}
      data-testid={`meal-plan-pricing-${mealPlan}`}
    >
      {/* Meal Plan Header */}
      <div className="bg-[color-mix(in_srgb,var(--navy)_10%,white)] text-[var(--navy)] px-4 py-2 font-semibold">
        {mealPlanLabel || mealPlan}
      </div>

      {/* Mobile stacked cards (<640px) — avoids clipped columns at 360px */}
      <div className="sm:hidden p-2">
        <PricingMobileCards
          type="meal_plan"
          categories={categories}
          format={isPersonWise ? 'person_wise' : 'total'}
          showBreakup={showBreakup}
          paxConfig={paxConfig}
          gstConfig={gstConfig}
          discountConfig={discountConfig}
        />
      </div>

      {/* Table */}
      <div className="hidden sm:block overflow-x-auto">
        {isPersonWise ? renderPersonWiseTable() : renderTotalAmountTable()}
      </div>
    </div>
  );
};

export default MealPlanPricingTable;
